<template>
  <div class="draw-module">
    <PixelCatRunner :speed="0.9" :cat-type="3" />
    <div class="draw-container">
      <!-- 左侧：抽卡区域 -->
      <div class="draw-left">
        <div class="draw-header">
          <span class="draw-header-icon">🎴</span>
          <span class="draw-header-title">每日试音抽卡</span>
          <span class="draw-header-badge">每日限1次</span>
        </div>

        <div class="draw-stage">
          <!-- 卡片区域：三阶段共存在 DOM 中，通过 opacity/transform 切换 -->
          <div class="draw-card-area">
            <!-- 阶段0：初始单卡片 -->
            <div
              class="phase-layer phase-idle"
              :class="{ 'phase-active': phase === 'idle', 'phase-exit': phase !== 'idle' }"
            >
              <div class="single-card" @click="doDraw">
                <div class="single-card-inner">
                  <span class="single-card-star">⭐</span>
                  <span class="single-card-question">?</span>
                  <span class="single-card-label">点击翻牌</span>
                  <span class="single-card-star">⭐</span>
                </div>
              </div>
            </div>

            <!-- 阶段1：多卡片横向滚动 -->
            <div
              class="phase-layer phase-scrolling"
              :class="{ 'phase-active': phase === 'scrolling' }"
            >
              <div class="scroll-viewport" ref="scrollViewport">
                <div class="scroll-track" ref="scrollTrack" :class="{ decelerating: scrollDecelerating }" :style="{ transform: 'translateX(' + scrollOffset + 'px)' }">
                  <div
                    v-for="(card, idx) in scrollingCards"
                    :key="'sc-' + idx"
                    class="scroll-card"
                    :class="{ winner: idx === winnerIndex && scrollDecelerating && drawResult, 'empty-winner': idx === winnerIndex && scrollDecelerating && !drawResult }"
                  >
                    <template v-if="idx === winnerIndex && scrollDecelerating && !drawResult">
                      <div class="scroll-card-avatar empty-avatar"></div>
                      <div class="scroll-card-name empty-card-name"></div>
                      <div class="scroll-card-timbre empty-card-timbre"></div>
                    </template>
                    <template v-else>
                      <div class="scroll-card-avatar">
                        <div class="scroll-avatar-placeholder">{{ card.name.charAt(0) }}</div>
                      </div>
                      <div class="scroll-card-name">{{ card.name }}</div>
                      <div class="scroll-card-timbre">{{ card.timbre }}</div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- 阶段2：翻牌结果 -->
            <div
              class="phase-layer phase-result"
              :class="{ 'phase-active': phase === 'result' }"
            >
              <!-- 空结果：纯灰色空卡片 -->
              <div class="empty-result-card" v-if="phase === 'result' && !drawResult && !drawing">
                <div class="empty-card-inner">
                  <div class="empty-title">明天再来看看吧~</div>
                  <div class="empty-desc">每天都有新的惊喜等你发现</div>
                  <div class="empty-hint">✨ 敬请期待 ✨</div>
                </div>
              </div>

              <!-- 翻牌动画容器 -->
              <div class="flip-card-container" v-if="drawResult">
                <div class="flip-card" :class="{ flipped: cardFlipped }">
                  <div class="flip-card-back">
                    <span class="flip-back-icon">🎴</span>
                  </div>
                  <div class="flip-card-front" :class="{ 'glow-faded': glowFaded }">
                    <!-- 金色光效层 -->
                    <div class="golden-aura" v-if="!glowFaded"></div>
                    <div class="golden-rays" v-if="!glowFaded"></div>

                    <!-- 结果卡片内容（与 SingerList 卡片样式一致） -->
                    <div class="front-content">
                      <div class="front-avatar-wrapper" :class="{ 'golden-border': !glowFaded }">
                        <img v-if="drawResult.avatar" :src="drawResult.avatar" :alt="drawResult.name" @error="hideEl" />
                        <div v-else class="front-avatar-placeholder">{{ drawResult.name.charAt(0) }}</div>
                      </div>
                      <div class="front-name">{{ drawResult.name }}</div>
                      <div class="front-tags">
                        <span v-for="(t, ti) in splitTags(drawResult.genre).slice(0, 2)" :key="'g-' + ti" class="front-tag genre">{{ t }}</span>
                        <span v-for="(t, ti) in splitTags(drawResult.timbre).slice(0, 3)" :key="'t-' + ti" class="front-tag timbre">{{ t }}</span>
                      </div>
                      <div v-if="drawResult.schedule" class="front-schedule">🕐 {{ drawResult.schedule }}</div>
                      <div class="front-voice" v-if="drawResult.voice">
                        <span class="front-voice-name">{{ drawResult.voice.voiceName }}</span>
                      </div>
                      <button class="front-play-btn" :class="{ playing: isPlaying }" @click="togglePlay" v-if="drawResult.voice">
                        <span v-if="!isPlaying">▶ 试听</span>
                        <span v-else>⏸ 暂停</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <audio ref="audioPlayer" :src="drawResult && drawResult.voice ? drawResult.voice.voiceUrl : ''" @ended="onAudioEnded" @error="onAudioError" preload="none" v-if="drawResult && drawResult.voice"></audio>
            </div>
          </div>

          <!-- 按钮 -->
          <div class="draw-actions">
            <button
              class="draw-btn"
              :class="{ cooling: drawCooldown || drawnToday }"
              :disabled="drawing || drawCooldown || drawnToday"
              @click="doDraw"
            >
              <span v-if="drawing" class="btn-content">
                <span class="btn-spinner"></span>
                抽卡中...
              </span>
              <span v-else-if="drawnToday" class="btn-content">
                <span class="btn-icon">🌙</span>
                今日已抽卡
              </span>
              <span v-else-if="drawCooldown" class="btn-content">
                <span class="btn-icon">⏳</span>
                冷却中 {{ cooldownSeconds }}s
              </span>
              <span v-else class="btn-content">
                <span class="btn-icon">🎴</span>
                立即抽卡
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- 右侧：抽卡记录 -->
      <div class="draw-right">
        <div class="draw-right-header">
          <span>📋 抽卡记录</span>
          <button class="refresh-btn" @click="loadDrawRecords" :disabled="loadingRecords">⟳</button>
        </div>
        <div class="records-list" v-if="drawRecords.length > 0">
          <div v-for="record in drawRecords" :key="record.id" class="record-singer-group">
            <div class="record-singer-header">
              <div class="record-singer-avatar-sm">
                <img v-if="record.avatar" :src="record.avatar" :alt="record.name" @error="hideEl" />
                <span v-else class="record-avatar-placeholder-sm">{{ record.name.charAt(0) }}</span>
              </div>
              <span class="record-singer-name">{{ record.name }}</span>
            </div>
            <div
              v-for="(voice, vi) in record.voices"
              :key="vi"
              class="record-voice-item"
              :class="{ 'now-playing': recordPlayingId === record.id + '-' + vi }"
              @click="playRecordVoice(record, voice, vi)"
            >
              <span class="record-play-icon">
                {{ recordPlayingId === record.id + '-' + vi ? '⏸' : '▶' }}
              </span>
              <span class="record-voice-name">{{ voice.voiceName }}</span>
              <span class="record-voice-count">×{{ voice.count }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="!loadingRecords" class="records-empty">暂无抽卡记录</div>
        <div v-else class="records-loading">加载中...</div>
        <audio ref="recordAudio" @ended="onRecordAudioEnded" @error="onRecordAudioError" preload="none"></audio>
      </div>
    </div>
  </div>
</template>

<script>
import API from '../api/api';

import PixelCatRunner from './PixelCatRunner.vue';

export default {
  name: 'DrawModule',
  components: { PixelCatRunner },
  data() {
    return {
      phase: 'idle',        // idle | scrolling | result
      drawing: false,
      drawResult: null,
      cardFlipped: false,
      glowFaded: false,
      drawCooldown: false,
      cooldownSeconds: 0,
      cooldownTimer: null,
      drawnToday: false,
      drawRecords: [],
      loadingRecords: false,
      isPlaying: false,
      // 滚动相关
      scrollingCards: [],
      winnerIndex: -1,
      scrollOffset: 0,
      scrollSpeed: 0,
      rafId: null,
      scrollDecelerating: false,
      // 记录播放
      recordPlayingId: null,
    };
  },
  methods: {
    splitTags(str) {
      if (!str) return [];
      return str.split(/[,，、\/]+/).map(function (t) { return t.trim(); }).filter(Boolean);
    },

    async doDraw() {
      if (this.drawing || this.drawCooldown || this.drawnToday) return;

      this.drawing = true;
      this.cardFlipped = false;
      this.glowFaded = false;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      // 先请求结果
      var result;
      try {
        result = await API.drawCard();
        this.drawResult = result;
      } catch (e) {
        console.error('抽卡失败:', e);
        this.drawing = false;
        this.phase = 'idle';
        if (e.message && e.message.indexOf('今日已抽卡') !== -1) {
          this.drawnToday = true;
        }
        this.$emit('show-toast', e.message || '抽卡失败，请稍后重试');
        this.startCooldown();
        return;
      }

      // 构建滚动卡片数据（3组副本，90张，实现无缝无限循环）
      await this.loadSingersForScroll();
      // winnerIndex 指向第2组副本中的某张卡片
      var singleSetCount = this.scrollingCards.length / 3;
      this.winnerIndex = singleSetCount + 15 + Math.floor(Math.random() * 8);

      // 有结果时才替换 winner 卡片内容，为空时保持原始卡片（最终展示灰色安慰卡片）
      if (this.drawResult) {
        this.scrollingCards[this.winnerIndex].name = this.drawResult.name;
        this.scrollingCards[this.winnerIndex].avatar = this.drawResult.avatar;
        this.scrollingCards[this.winnerIndex].timbre = this.drawResult.timbre || '';
      }

      // 进入滚动阶段，初始偏移从第2组开头开始
      this.phase = 'scrolling';
      this.scrollDecelerating = false;
      this.scrollOffset = -(singleSetCount * this.getCardWidth());
      this.scrollSpeed = 18;

      await this.$nextTick();
      this.startRAFScroll();

      // 高速滚动 2.2 秒后开始减速
      await new Promise(function (resolve) { return setTimeout(resolve, 2200); });
      this.startDeceleration();
    },

    async loadSingersForScroll() {
      try {
        var singers = await API.getSingers();
        // 渲染3组卡片（90张）实现无缝无限循环滚动
        this.scrollingCards = [];
        for (var i = 0; i < 90; i++) {
          var s = singers[i % singers.length];
          this.scrollingCards.push({
            name: s.name,
            avatar: s.avatar,
            timbre: s.timbre || '',
          });
        }
      } catch (e) {
        // 降级：用 drawResult 填充
        this.scrollingCards = [];
        for (var i = 0; i < 90; i++) {
          this.scrollingCards.push({
            name: this.drawResult.name,
            avatar: this.drawResult.avatar,
          });
        }
      }
    },

    // 动态获取每张滚动卡片的实际占用宽度（卡片宽度 + gap）
    getCardWidth() {
      var el = this.$refs.scrollTrack;
      if (el && el.children && el.children.length > 0) {
        var firstCard = el.children[0];
        var secondCard = el.children[1];
        if (secondCard) {
          return secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().left;
        }
      }
      // 降级默认值
      return 144;
    },

    // ================================================================
    // requestAnimationFrame 驱动的流畅滚动
    // ================================================================
    startRAFScroll() {
      var self = this;
      var cardWidth = this.getCardWidth();
      var singleSetCount = this.scrollingCards.length / 3;
      var singleSetWidth = singleSetCount * cardWidth;

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      function tick() {
        if (self.phase !== 'scrolling') {
          self.rafId = null;
          return;
        }

        self.scrollOffset -= self.scrollSpeed;
        // 保持在 [-(singleSetWidth * 2), 0] 范围，确保视口始终有卡片
        // 当偏移超过第3组区域时，无缝跳回等价的第2组位置
        if (self.scrollOffset < -(singleSetWidth * 2)) {
          self.scrollOffset += singleSetWidth;
        }

        self.rafId = requestAnimationFrame(tick);
      }

      this.rafId = requestAnimationFrame(tick);
    },

    startDeceleration() {
      var self = this;
      var cardWidth = this.getCardWidth();
      var singleSetCount = this.scrollingCards.length / 3;
      var singleSetWidth = singleSetCount * cardWidth;

      // 停止高速滚动 RAF
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      // 目标：让第 winnerIndex 张卡片停在视口中央
      var viewportWidth = this.$refs.scrollViewport
        ? this.$refs.scrollViewport.clientWidth
        : 400;
      var targetOffset = -(this.winnerIndex * cardWidth) + (viewportWidth / 2) - (cardWidth / 2);

      // 当前 scrollOffset 规约到合理范围
      while (this.scrollOffset < -(singleSetWidth * 2)) {
        this.scrollOffset += singleSetWidth;
      }
      while (this.scrollOffset > 0) {
        this.scrollOffset -= singleSetWidth;
      }

      // 目标始终在当前位置左侧
      while (targetOffset > this.scrollOffset) {
        targetOffset -= singleSetWidth;
      }
      while (targetOffset + singleSetWidth <= this.scrollOffset) {
        targetOffset += singleSetWidth;
      }

      // 确保减速距离足够（至少滚2屏以上），否则多绕一轮
      var remaining = this.scrollOffset - targetOffset;
      if (remaining < viewportWidth * 2) {
        targetOffset -= singleSetWidth;
      }

      // 基于速度渐进衰减：从当前速度（18px/帧）线性递减到 0
      var currentSpeed = this.scrollSpeed; // 18
      var totalFrames = 0;
      // 估算总帧数：S = v0 * n / 2 → n = 2S / v0（匀减速到0的位移公式）
      var distance = this.scrollOffset - targetOffset;
      totalFrames = Math.round((2 * distance) / currentSpeed);
      if (totalFrames < 30) totalFrames = 30;

      var elapsedFrames = 0;

      this.scrollDecelerating = true;

      function decelTick() {
        elapsedFrames++;
        var remainFrames = totalFrames - elapsedFrames;
        if (remainFrames <= 0) {
          self.scrollOffset = targetOffset;
          self.phase = 'result';
          self.drawing = false;
          self.scrollDecelerating = false;

          // 有结果时才触发翻牌动画，为空时直接展示灰色安慰卡片
          if (self.drawResult) {
            self.$nextTick(function () {
              setTimeout(function () {
                self.cardFlipped = true;
                setTimeout(function () {
                  self.glowFaded = true;
                }, 3500);
              }, 300);
            });
          }

          self.drawnToday = true;
          self.startCooldown();
          // 重新加载记录（仅刷新右侧历史列表，不覆盖当前抽卡结果）
          self.loadDrawRecords(true);
          return;
        }

        // 匀减速：下一帧速度 = 剩余距离 / 剩余帧数 × 2
        var remaining = self.scrollOffset - targetOffset;
        var speedThisFrame = (2 * remaining) / (remainFrames + 1);
        if (speedThisFrame < 0.5) speedThisFrame = 0.5;
        self.scrollOffset -= speedThisFrame;

        // 无缝循环：始终保持 offset 在合理范围
        while (self.scrollOffset < -(singleSetWidth * 2)) {
          self.scrollOffset += singleSetWidth;
          targetOffset += singleSetWidth;
        }

        self.rafId = requestAnimationFrame(decelTick);
      }

      this.rafId = requestAnimationFrame(decelTick);
    },

    startCooldown() {
      this.drawCooldown = true;
      this.cooldownSeconds = 3;
      clearInterval(this.cooldownTimer);
      var self = this;
      this.cooldownTimer = setInterval(function () {
        self.cooldownSeconds -= 1;
        if (self.cooldownSeconds <= 0) {
          clearInterval(self.cooldownTimer);
          self.drawCooldown = false;
        }
      }, 1000);
    },

    togglePlay() {
      var audio = this.$refs.audioPlayer;
      if (!audio) return;
      if (audio.paused) {
        var self = this;
        audio.play().then(function () { self.isPlaying = true; })
          .catch(function () { self.isPlaying = false; });
      } else {
        audio.pause();
        this.isPlaying = false;
      }
    },

    onAudioEnded() { this.isPlaying = false; },
    onAudioError() {
      this.isPlaying = false;
      this.$emit('show-toast', '音频加载失败，请稍后重试');
    },

    playRecordVoice(record, voice, vi) {
      var recordAudio = this.$refs.recordAudio;
      if (!recordAudio) return;
      var playingKey = record.id + '-' + vi;
      if (this.recordPlayingId === playingKey) {
        if (recordAudio.paused) {
          recordAudio.play().catch(function () {});
        } else {
          recordAudio.pause();
          this.recordPlayingId = null;
        }
        return;
      }
      recordAudio.src = voice.voiceUrl;
      this.recordPlayingId = playingKey;
      recordAudio.play().catch(function () {});
    },

    onRecordAudioEnded() { this.recordPlayingId = null; },
    onRecordAudioError() {
      this.recordPlayingId = null;
      this.$emit('show-toast', '音频加载失败，请稍后重试');
    },

    async loadDrawRecords(skipTodayUpdate) {
      this.loadingRecords = true;
      try {
        var data = await API.getDrawRecords();
        // 新接口返回 { todayRecord, historyRecords }
        if (data && data.historyRecords) {
          this.drawRecords = data.historyRecords || [];
          // 处理今日数据（仅在非跳过模式下更新，避免覆盖 doDraw 设置的状态）
          if (!skipTodayUpdate) {
            if (data.todayRecord) {
              this.drawnToday = true;
              // 判断是否为空卡：仅 id 有值，其余属性全为空
              var tr = data.todayRecord;
              var isEmptyCard = tr.id && !tr.name && !tr.timbre && !tr.genre
                && !tr.schedule && !tr.avatar && (tr.order === null || tr.order === undefined)
                && !tr.voice;
              if (isEmptyCard) {
                // 今日抽了空卡：展示空结果
                this.drawResult = null;
              } else {
                this.drawResult = tr;
              }
              this.phase = 'result';
              if (this.drawResult) {
                this.cardFlipped = true;
                this.glowFaded = true;
              }
            } else {
              // 今日未抽卡
              this.drawnToday = false;
              this.drawResult = null;
              this.cardFlipped = false;
              this.glowFaded = false;
              this.phase = 'idle';
            }
          }
        } else {
          // 兼容旧接口格式（数组）
          this.drawRecords = data || [];
        }
      } catch (e) {
        console.error('加载抽卡记录失败:', e);
      } finally {
        this.loadingRecords = false;
      }
    },

    hideEl(e) {
      var el = e.target;
      if (el && el.tagName === 'IMG') {
        el.style.display = 'none';
      }
    },
  },

  mounted() { this.loadDrawRecords(); },

  beforeDestroy() {
    clearInterval(this.cooldownTimer);
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    var audio = this.$refs.audioPlayer;
    if (audio && !audio.paused) audio.pause();
    var recordAudio = this.$refs.recordAudio;
    if (recordAudio && !recordAudio.paused) recordAudio.pause();
  },
};
</script>

<style scoped>
.draw-module { margin: 20px 0; }

.draw-container {
  display: flex;
  gap: 16px;
  background: linear-gradient(135deg, var(--glass-strong), var(--glass-bg));
  border: 3px solid var(--pixel-dark);
  border-radius: 8px;
  padding: 16px;
  box-shadow:
    0 0 0 2px var(--pixel-ice),
    0 0 0 4px var(--pixel-blue),
    0 5px 20px var(--shadow-soft),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.draw-left { flex: 1; min-width: 0; }

.draw-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.draw-header-icon {
  font-size: 22px;
  animation: drawIconFloat 3s ease-in-out infinite;
}

@keyframes drawIconFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-4px) rotate(5deg); }
}

.draw-header-title { font-size: 15px; font-weight: 700; color: var(--pixel-dark); }

.draw-header-badge {
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 3px;
  border: 2px solid var(--pixel-dark);
  background: linear-gradient(180deg, var(--pixel-pink), var(--pixel-lavender));
  color: var(--pixel-dark);
  font-weight: 700;
}

/* ================================================================
   舞台
   ================================================================ */
.draw-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.draw-card-area {
  width: 100%;
  min-height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* ================================================================
   阶段层 — 所有阶段共存，通过 opacity/transform 过渡
   ================================================================ */
.phase-layer {
  position: absolute;
  top: 0; left: 0; right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 340px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.45s ease, transform 0.45s ease;
}

/* idle → scrolling：初始卡片缩小淡出，滚动层放大淡入 */
.phase-idle {
  transform: scale(1);
  opacity: 1;
  pointer-events: auto;
}

.phase-idle.phase-exit {
  opacity: 0;
  transform: scale(0.7);
  pointer-events: none;
}

.phase-scrolling {
  transform: scale(0.7);
  opacity: 0;
  overflow-x: hidden;
  overflow-y: visible;
  border-radius: 6px;
}

.phase-scrolling.phase-active {
  opacity: 1;
  transform: scale(1);
}

/* scrolling → result：滚动层淡出，结果层淡入 */
.phase-result {
  opacity: 0;
  transform: translateY(30px);
}

.phase-result.phase-active {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

/* ================================================================
   阶段0：初始单卡片
   ================================================================ */
.single-card {
  width: 170px;
  height: 230px;
  background: linear-gradient(135deg, #7B93DB, #5A6FB8, #D5C8F0, #8BB5E8);
  background-size: 300% 300%;
  animation: singleGradient 4s ease infinite alternate;
  border-radius: 8px;
  border: 3px solid var(--pixel-dark);
  box-shadow:
    inset 0 3px 0 rgba(255, 255, 255, 0.35),
    0 6px 0 var(--pixel-shadow-dark),
    0 8px 30px rgba(123, 147, 219, 0.4),
    0 0 60px rgba(139, 181, 232, 0.35);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
}

.single-card:hover {
  transform: translateY(-8px) scale(1.05);
  box-shadow:
    inset 0 3px 0 rgba(255, 255, 255, 0.35),
    0 8px 0 var(--pixel-shadow-dark),
    0 16px 45px rgba(123, 147, 219, 0.55),
    0 0 80px rgba(139, 181, 232, 0.5);
}

.single-card:active { transform: scale(0.93); }

@keyframes singleGradient {
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
}

.single-card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.single-card-star {
  font-size: 26px;
  filter: drop-shadow(0 0 10px rgba(255, 216, 144, 0.8));
  animation: starPulse 1.5s ease-in-out infinite alternate;
}

.single-card-star:last-child { animation-delay: 0.5s; }

@keyframes starPulse {
  0% { transform: scale(0.8); opacity: 0.7; }
  100% { transform: scale(1.2); opacity: 1; }
}

.single-card-question {
  font-size: 52px;
  font-weight: 900;
  color: white;
  text-shadow: 0 4px 20px rgba(255, 255, 255, 0.8);
  font-family: 'ZCOOL KuaiLe', cursive;
  animation: questionFloat 2s ease-in-out infinite;
}

@keyframes questionFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.single-card-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* ================================================================
   阶段1：多卡片横向滚动
   ================================================================ */
.scroll-viewport {
  width: 100%;
  overflow: visible;
  position: relative;
}

.scroll-track {
  display: flex;
  gap: 24px;
  align-items: center;
  padding: 20px 0;
  will-change: transform;
}

.scroll-card {
  width: 120px;
  height: 170px;
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--glass-strong), var(--glass-bg));
  border-radius: 6px;
  border: 3px solid var(--pixel-dark);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 3px 0 var(--pixel-shadow-dark),
    0 3px 12px var(--shadow-soft);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 8px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease, background 0.3s ease;
}

.scroll-card.winner {
  border-color: #FFD890;
  box-shadow:
    inset 0 2px 0 rgba(255,255,255,0.5),
    0 3px 0 #B8860B,
    0 0 28px rgba(255, 216, 144, 0.85),
    0 0 56px rgba(255, 216, 144, 0.5);
  background: linear-gradient(180deg, #FFF8E7, #FFFDF5);
  transform: scale(1.15);
  z-index: 2;
  animation: winnerPulse 0.6s ease-in-out infinite alternate;
}

@keyframes winnerPulse {
  0% { box-shadow: inset 0 2px 0 rgba(255,255,255,0.5), 0 3px 0 #B8860B, 0 0 24px rgba(255, 216, 144, 0.7), 0 0 48px rgba(255, 216, 144, 0.4); }
  100% { box-shadow: inset 0 2px 0 rgba(255,255,255,0.5), 0 3px 0 #B8860B, 0 0 38px rgba(255, 216, 144, 1), 0 0 70px rgba(255, 200, 100, 0.6); }
}

/* 空数据时的灰色落点卡片 */
.scroll-card.empty-winner {
  border-color: #bbb;
  box-shadow:
    inset 0 2px 0 rgba(255,255,255,0.5),
    0 3px 0 #999,
    0 0 20px rgba(150, 150, 150, 0.4),
    0 0 40px rgba(150, 150, 150, 0.2);
  background: linear-gradient(180deg, #e0e0e0, #d0d0d0);
  transform: scale(1.15);
  z-index: 2;
}

.scroll-card .empty-avatar {
  background: #c8c8c8;
  border-color: #bbb;
}

.scroll-card .empty-card-name {
  background: #d0d0d0;
  border-radius: 6px;
  height: 14px;
  width: 60%;
}

.scroll-card .empty-card-timbre {
  background: #d8d8d8;
  border-radius: 8px;
  height: 10px;
  width: 80%;
}

.scroll-card-avatar {
  width: 60px;
  height: 60px;
  border-radius: 3px;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 3px 10px var(--shadow-soft);
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--dream-light), var(--dream-primary), var(--dream-lavender));
  background-size: 200% 200%;
}

.scroll-card.winner .scroll-card-avatar {
  border-color: #FFD890;
}

.scroll-avatar-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; color: white; font-weight: 700;
  font-family: 'ZCOOL KuaiLe', cursive;
  text-shadow: 0 1px 4px rgba(74, 90, 138, 0.5);
}

.scroll-card-name {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.scroll-card-timbre {
  font-size: 10px;
  color: var(--text-light);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  background: rgba(139, 181, 232, 0.12);
  border-radius: 3px;
  padding: 2px 8px;
  border: 1px solid var(--pixel-dark);
}

.scroll-card.winner .scroll-card-name {
  color: #B8860B;
}

.scroll-card.winner .scroll-card-timbre {
  color: #C8A040;
  background: rgba(255, 216, 144, 0.2);
}

/* ================================================================
   阶段2：翻牌结果
   ================================================================ */
.flip-card-container {
  perspective: 1200px;
  width: 220px;
  height: 310px;
}

.flip-card {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.flip-card.flipped { transform: rotateY(180deg); }

/* 卡背 */
.flip-card-back {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 8px;
  background: linear-gradient(135deg, #7B93DB, #5A6FB8, #D5C8F0, #8BB5E8);
  background-size: 300% 300%;
  animation: flipBackGradient 4s ease infinite alternate;
  border: 3px solid var(--pixel-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    inset 0 3px 0 rgba(255, 255, 255, 0.35),
    0 6px 0 var(--pixel-shadow-dark),
    0 8px 30px rgba(123, 147, 219, 0.4);
}

.flip-back-icon {
  font-size: 56px;
  filter: drop-shadow(0 4px 16px rgba(255, 255, 255, 0.8));
}

@keyframes flipBackGradient {
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
}

/* 卡面（与 SingerList .singer-card 样式一致） */
.flip-card-front {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  transform: rotateY(180deg);
  border-radius: 8px;
  background: linear-gradient(135deg, var(--glass-strong), var(--glass-bg), rgba(255, 255, 255, 0.5));
  border: 3px solid var(--pixel-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 4px 0 var(--pixel-shadow-dark),
    0 5px 20px var(--shadow-soft);
  transition: border-color 1.5s ease, box-shadow 1.5s ease, background 1.5s ease;
}

/* 金色光效状态 */
.flip-card-front:not(.glow-faded) {
  border-color: #FFD890;
  background: linear-gradient(180deg, #FFF8E7, #FFFDF5, #FFF8E7);
  box-shadow:
    inset 0 3px 0 rgba(255,255,255,0.5),
    0 4px 0 #B8860B,
    0 0 35px rgba(255, 216, 144, 0.6),
    0 0 70px rgba(255, 216, 144, 0.35),
    0 0 120px rgba(255, 200, 100, 0.2);
  animation: goldenGlowPulse 2s ease-in-out infinite;
}

@keyframes goldenGlowPulse {
  0% { box-shadow: 0 0 30px rgba(255, 216, 144, 0.5), 0 0 60px rgba(255, 216, 144, 0.3); }
  50% { box-shadow: 0 0 50px rgba(255, 216, 144, 0.85), 0 0 100px rgba(255, 200, 100, 0.5), 0 0 150px rgba(255, 180, 50, 0.3); }
  100% { box-shadow: 0 0 30px rgba(255, 216, 144, 0.5), 0 0 60px rgba(255, 216, 144, 0.3); }
}

/* 金色光晕 */
.golden-aura {
  position: absolute;
  inset: -30px;
  background: radial-gradient(ellipse at center, rgba(255, 216, 144, 0.3), rgba(255, 200, 100, 0.1), transparent 70%);
  animation: auraPulse 2s ease-in-out infinite;
  pointer-events: none;
  transition: opacity 1.5s ease;
}

@keyframes auraPulse {
  0% { transform: scale(0.8); opacity: 0.4; }
  50% { transform: scale(1.3); opacity: 0.85; }
  100% { transform: scale(0.8); opacity: 0.4; }
}

.golden-rays {
  position: absolute;
  inset: -50px;
  background: conic-gradient(from 0deg,
    transparent 0deg,
    rgba(255, 216, 144, 0.15) 20deg,
    transparent 40deg,
    rgba(255, 216, 144, 0.1) 60deg,
    transparent 80deg,
    rgba(255, 216, 144, 0.12) 100deg,
    transparent 140deg,
    rgba(255, 216, 144, 0.08) 180deg,
    transparent 220deg,
    rgba(255, 216, 144, 0.12) 260deg,
    transparent 300deg);
  animation: raysSpin 8s linear infinite;
  pointer-events: none;
  transition: opacity 1.5s ease;
}

@keyframes raysSpin {
  to { transform: rotate(360deg); }
}

/* glowFaded 时隐藏光效层 */
.flip-card-front.glow-faded .golden-aura,
.flip-card-front.glow-faded .golden-rays {
  opacity: 0;
}

/* 光效消退后恢复正常 SingerList 卡片样式 */
.flip-card-front.glow-faded {
  border-color: var(--pixel-dark);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 4px 0 var(--pixel-shadow-dark),
    0 5px 20px var(--shadow-soft);
  background: linear-gradient(135deg, var(--glass-strong), var(--glass-bg), rgba(255, 255, 255, 0.5));
}

/* 内容区（匹配 SingerList .singer-card） */
.front-content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* 头像（匹配 SingerList .singer-avatar-wrapper） */
.front-avatar-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  border: 4px solid white;
  box-shadow: 0 6px 20px var(--shadow-soft), 0 0 30px var(--glow-pink);
  background: linear-gradient(135deg, var(--dream-light), var(--dream-primary), var(--dream-lavender), var(--pixel-blue));
  background-size: 300% 300%;
  flex-shrink: 0;
  animation: avatarGradientShift 4s ease-in-out infinite;
  transition: border-color 1.5s ease, box-shadow 1.5s ease;
}

.front-avatar-wrapper.golden-border {
  border-color: #FFD890;
  box-shadow: 0 6px 20px rgba(255, 216, 144, 0.5), 0 0 40px rgba(255, 216, 144, 0.4);
}

@keyframes avatarGradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.front-avatar-wrapper img { width: 100%; height: 100%; object-fit: cover; }

.front-avatar-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; color: white; font-weight: 700;
  font-family: 'ZCOOL KuaiLe', cursive;
  text-shadow: 0 3px 10px rgba(74, 90, 138, 0.6);
}

.front-name {
  font-size: 17px;
  font-weight: 700;
  color: var(--pixel-dark);
  text-shadow: 0 1px 2px rgba(139, 181, 232, 0.3);
}

.front-tags {
  display: flex; flex-wrap: wrap; justify-content: center; gap: 5px;
}

.front-tag {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 3px;
  background: var(--glass-bg);
  color: var(--text-secondary);
  border: 2px solid var(--pixel-dark);
}

.front-tag.genre {
  background: linear-gradient(135deg, rgba(213, 200, 240, 0.45), rgba(168, 184, 232, 0.35));
  color: #7B5DB8;
}

.front-tag.timbre {
  background: linear-gradient(135deg, rgba(200, 240, 232, 0.45), rgba(184, 232, 216, 0.35));
  color: #2E8B57;
}

.front-schedule { font-size: 12px; color: var(--text-secondary); }

.front-voice {
  display: flex; align-items: center; gap: 4px;
}

.front-voice-name { font-size: 12px; color: var(--text-secondary); font-weight: 500; }

.front-play-btn {
  margin-top: 2px;
  padding: 8px 24px;
  border: 3px solid var(--pixel-dark);
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  color: var(--pixel-dark);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease;
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 3px 0 var(--pixel-shadow-dark);
}

.front-play-btn:hover { transform: translateY(-2px); filter: brightness(1.06); }
.front-play-btn:active { transform: translateY(2px); box-shadow: inset 0 3px 0 var(--pixel-shadow-web), 0 0 0 var(--pixel-shadow-dark); }
.front-play-btn.playing { background: linear-gradient(180deg, var(--pixel-pink), #FF9EB5); }

/* ================================================================
   按钮
   ================================================================ */
.draw-actions { width: 100%; display: flex; justify-content: center; }

.draw-btn {
  padding: 12px 40px;
  border: 3px solid var(--pixel-dark);
  border-radius: 4px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  color: var(--pixel-dark);
  transition: transform 0.1s, box-shadow 0.1s;
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 4px 0 var(--pixel-shadow-dark),
    0 8px 30px rgba(123, 147, 219, 0.45),
    0 0 50px rgba(139, 181, 232, 0.35);
  position: relative;
  overflow: hidden;
}

.draw-btn::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transform: translateX(-100%);
  animation: drawBtnShine 2.5s ease-in-out infinite;
}

@keyframes drawBtnShine {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

.draw-btn:hover:not(:disabled) {
  filter: brightness(1.06);
  transform: translateY(-2px);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 6px 0 var(--pixel-shadow-dark),
    0 14px 45px rgba(123, 147, 219, 0.55),
    0 0 80px rgba(255, 214, 236, 0.55);
}

.draw-btn:active:not(:disabled) {
  transform: translateY(3px);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 1px 0 var(--pixel-shadow-dark);
}

.draw-btn:disabled { cursor: not-allowed; }

.draw-btn.cooling {
  background: linear-gradient(180deg, #C8D8F0, #A8B8D8);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 4px 0 var(--pixel-shadow-dark),
    0 4px 16px rgba(123, 147, 219, 0.25);
}

.draw-btn .btn-content { display: inline-flex; align-items: center; gap: 8px; }
.draw-btn .btn-icon { font-size: 20px; }

.draw-btn .btn-spinner {
  width: 18px; height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: btnSpinner 0.7s linear infinite;
  display: inline-block;
}

@keyframes btnSpinner { to { transform: rotate(360deg); } }

/* ================================================================
   记录
   ================================================================ */
.draw-right {
  width: 260px; flex-shrink: 0;
  position: relative;
  background: var(--glass-strong);
  border-radius: 6px;
  border: 3px solid var(--pixel-dark);
  overflow: hidden;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 3px 0 var(--pixel-shadow-dark),
    0 4px 12px var(--shadow-soft);
}

.draw-right-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 3px solid var(--pixel-dark);
  background: var(--pixel-ice);
  font-size: 13px; font-weight: 700; color: var(--pixel-dark);
  position: relative;
  z-index: 2;
}

.refresh-btn {
  width: 28px; height: 28px;
  border: 2px solid var(--pixel-dark);
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  border-radius: 3px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: var(--pixel-dark);
  display: flex; align-items: center; justify-content: center;
  transition: background 0.3s ease, color 0.3s ease;
  font-family: inherit;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.refresh-btn:active {
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 0 0 var(--pixel-shadow-dark);
}

.refresh-btn:hover:not(:disabled) { filter: brightness(1.08); }

.records-list {
  position: absolute;
  top: 43px;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 8px 10px;
  overflow-y: auto;
}

.record-singer-group {
  margin-bottom: 10px;
  background: var(--glass-bg);
  border-radius: 4px;
  padding: 8px 10px;
  border: 2px solid var(--pixel-dark);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}
.record-singer-group:last-child { margin-bottom: 0; }

.record-singer-header {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 6px; padding-bottom: 6px;
  border-bottom: 2px dashed var(--pixel-dark);
}

.record-singer-avatar-sm {
  width: 28px; height: 28px; border-radius: 3px; overflow: hidden;
  border: 2px solid white; box-shadow: 0 2px 6px var(--shadow-soft); flex-shrink: 0;
  background: linear-gradient(135deg, var(--dream-light), var(--dream-primary));
}
.record-singer-avatar-sm img { width: 100%; height: 100%; object-fit: cover; }

.record-avatar-placeholder-sm {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; color: white; font-weight: 700;
}

.record-singer-name { font-size: 13px; font-weight: 600; color: var(--pixel-dark); }

.record-voice-item {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 4px 5px 8px; font-size: 12px;
  cursor: pointer; border-radius: 3px;
  transition: background 0.2s ease;
  border: 1px solid transparent;
}
.record-voice-item:hover { background: rgba(139, 181, 232, 0.15); border-color: var(--pixel-blue); }
.record-voice-item.now-playing {
  background: linear-gradient(90deg, rgba(255, 216, 144, 0.25), transparent);
  border-color: #FFD890;
}

.record-play-icon { font-size: 10px; width: 16px; flex-shrink: 0; color: var(--dream-accent); }
.record-voice-item.now-playing .record-play-icon { color: #E8B840; }

.record-voice-name {
  color: var(--text-primary); flex: 1;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.record-voice-count { color: var(--dream-accent); font-weight: 700; margin-left: 4px; flex-shrink: 0; }

.records-empty, .records-loading {
  position: absolute;
  top: 43px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center; color: var(--text-light); font-size: 13px;
}

/* ================================================================
   空结果灰色安慰卡片
   ================================================================ */
.empty-result-card {
  width: 220px;
  min-height: 310px;
  background: linear-gradient(180deg, #e8e8e8, #d0d0d0, #c8c8c8);
  border-radius: 8px;
  border: 3px solid #bbb;
  box-shadow:
    inset 0 3px 0 rgba(255,255,255,0.4),
    0 4px 0 #999,
    0 4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: emptyCardFadeIn 0.5s ease;
}

@keyframes emptyCardFadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.empty-card-inner {
  text-align: center;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.empty-title {
  font-size: 17px;
  font-weight: 700;
  color: #888;
  letter-spacing: 1px;
}

.empty-desc {
  font-size: 13px;
  color: #999;
  line-height: 1.8;
}

.empty-hint {
  font-size: 12px;
  color: #aaa;
  letter-spacing: 2px;
}

@media (max-width: 768px) {
  .empty-result-card { width: 180px; min-height: 260px; }
  .empty-title { font-size: 15px; }
  .empty-desc { font-size: 12px; }
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 768px) {
  .draw-container { flex-direction: column; }
  .draw-right { width: 100%; min-height: 280px; }
  .flip-card-container { width: 180px; height: 260px; }
  .single-card { width: 140px; height: 190px; }
  .single-card-question { font-size: 40px; }
  .single-card-inner { gap: 6px; }
  .single-card-star { font-size: 20px; }
  .scroll-card { width: 100px; height: 145px; gap: 6px; padding: 10px 6px; }
  .scroll-card-avatar { width: 50px; height: 50px; border-width: 2px; }
  .scroll-avatar-placeholder { font-size: 18px; }
  .scroll-card-name { font-size: 10px; }
  .front-avatar-wrapper { width: 64px; height: 64px; }
  .front-name { font-size: 15px; }
  .front-content { padding: 14px 10px; gap: 6px; }
}

@media (max-width: 480px) {
  .flip-card-container { width: 150px; height: 220px; }
  .single-card { width: 120px; height: 165px; }
  .single-card-question { font-size: 34px; }
  .single-card-inner { gap: 4px; }
  .single-card-star { font-size: 16px; }
  .scroll-card { width: 84px; height: 122px; gap: 4px; padding: 8px 4px; }
  .scroll-card-avatar { width: 42px; height: 42px; }
  .scroll-avatar-placeholder { font-size: 15px; }
  .scroll-card-name { font-size: 9px; }
  .scroll-card-timbre { font-size: 8px; padding: 1px 4px; }
  .front-avatar-wrapper { width: 54px; height: 54px; }
  .front-name { font-size: 14px; }
  .front-content { padding: 10px 8px; gap: 5px; }
  .front-tag { font-size: 9px; padding: 2px 6px; }
  .front-play-btn { padding: 4px 10px; font-size: 11px; }
  .empty-result-card { width: 150px; min-height: 220px; }
  .empty-title { font-size: 14px; }
  .empty-desc { font-size: 11px; }
  .empty-card-inner { padding: 20px 12px; gap: 10px; }
}
</style>
